import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../api/client";
import type { Me } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import { SimulateSupplierResponsePanel } from "../simulation/SimulateSupplierResponsePanel";
import { POStatusBanner } from "../purchase-orders/POStatusBanner";
import { LoadingState } from "../../components/QueryStates";

export function NegotiationsPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [acceptedPos, setAcceptedPos] = useState<
    Record<
      string,
      {
        po_number: string;
        po_status: string;
        po_needs_manager_approval: boolean;
      }
    >
  >({});

  const canApprove = hasRole(me, "manager", "admin");
  const canAccept = hasRole(me, "buyer", "planner");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["negotiations"],
    queryFn: () => api.listNegotiations(),
    refetchInterval: 15_000,
  });
  const negotiations = data?.items ?? [];

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["negotiations"] });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveNegotiationCounterOffer(id),
    onSuccess: () => {
      setMsg("✅ Counter-offer approved.");
      invalidate();
    },
    onError: () => setMsg("❌ Failed to approve."),
  });

  // The negotiation list does not carry the underlying quote's review flag, so
  // unlike the quote and recommendation screens this cannot warn up front. It
  // reacts to the API's 409 instead: show the reason, then let the buyer
  // confirm and retry with the acknowledgement.
  const [reviewBlocked, setReviewBlocked] = useState<Record<string, string>>(
    {},
  );

  const acceptMutation = useMutation({
    mutationFn: ({ id, ack }: { id: string; ack: boolean }) =>
      api.acceptNegotiation(id, ack),
    onSuccess: (result, { id }) => {
      setAcceptedPos((prev) => ({ ...prev, [id]: result }));
      setReviewBlocked((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      setMsg(
        result.already_existed
          ? `ℹ️ This negotiation was already accepted — PO ${result.po_number} exists (no new PO created).`
          : `✅ PO ${result.po_number} created from negotiated price.`,
      );
      invalidate();
    },
    onError: (e, { id }) => {
      if (e instanceof ApiError && e.status === 409) {
        setReviewBlocked((prev) => ({ ...prev, [id]: e.detail }));
        setMsg(null);
        return;
      }
      setMsg("❌ " + e.message);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelNegotiation(id),
    onSuccess: () => {
      setMsg("Negotiation cancelled.");
      invalidate();
    },
    onError: () => setMsg("❌ Failed to cancel."),
  });

  const statusColor = (s: string) => {
    if (s === "active") return "#1976d2";
    if (s === "completed") return "#2e7d32";
    if (s === "cancelled" || s === "failed") return "#c62828";
    return "#666";
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>🤝 Negotiations</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
      </div>
      {msg && (
        <div style={{ ...S.card, background: "#e8f5e9", marginBottom: 12 }}>
          {msg}
        </div>
      )}
      {isLoading ? (
        <LoadingState label="Loading negotiations…" />
      ) : negotiations.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>No negotiations yet.</p>
          <p style={S.muted}>
            Start a negotiation from any quote in the RFQ Quotes view, or from a
            recommendation.
          </p>
        </div>
      ) : (
        negotiations.map((neg) => {
          // A PO from this session's accept, or one a previous session already
          // created — the server reports the latter so the button doesn't come
          // back after a reload and imply a second PO is still to be made.
          const po =
            acceptedPos[neg.id] ??
            (neg.po_number
              ? {
                  po_number: neg.po_number,
                  po_status: neg.po_status ?? "",
                  po_needs_manager_approval:
                    neg.po_status === "PendingApproval",
                }
              : null);
          return (
            <div key={neg.id} style={{ ...S.card, marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>
                    Negotiation{" "}
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: statusColor(neg.status) + "22",
                        color: statusColor(neg.status),
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 6,
                      }}
                    >
                      {neg.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={S.muted}>
                    Round {neg.current_round}/{neg.max_rounds} · Initial: $
                    {neg.initial_unit_price.toFixed(2)} · Best: $
                    {(neg.current_best_price ?? neg.initial_unit_price).toFixed(
                      2,
                    )}
                  </div>
                  {neg.pending_approval && (
                    <div
                      style={{
                        color: "#e65100",
                        fontWeight: 600,
                        marginTop: 4,
                      }}
                    >
                      ⚠ Requires manager approval ($
                      {neg.pending_approval_amount?.toFixed(2)})
                    </div>
                  )}
                  {neg.final_price && (
                    <div
                      style={{
                        color: "#2e7d32",
                        fontWeight: 600,
                        marginTop: 4,
                      }}
                    >
                      ✅ Final price: ${neg.final_price.toFixed(2)}
                    </div>
                  )}
                </div>
                <div
                  style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}
                >
                  {neg.pending_approval && canApprove && (
                    <button
                      style={S.btn}
                      onClick={() => approveMutation.mutate(neg.id)}
                      disabled={approveMutation.isPending}
                    >
                      Approve
                    </button>
                  )}
                  {neg.status === "completed" &&
                    neg.final_price !== null &&
                    canAccept &&
                    !po && (
                      <button
                        style={{ ...S.btn, background: "#2f9e44" }}
                        onClick={() =>
                          acceptMutation.mutate({ id: neg.id, ack: false })
                        }
                        disabled={acceptMutation.isPending}
                      >
                        ✓ Accept & Create PO
                      </button>
                    )}
                  {reviewBlocked[neg.id] && (
                    <div
                      role="alert"
                      style={{
                        background: "#fff4e6",
                        border: "1px solid #ffd8a8",
                        borderRadius: 4,
                        padding: "8px 10px",
                        marginTop: 8,
                        fontSize: 13,
                        color: "#8a5a00",
                        width: "100%",
                      }}
                    >
                      <strong>⚠ This quote needs review</strong> —{" "}
                      {reviewBlocked[neg.id]}
                      <div style={{ marginTop: 8 }}>
                        <button
                          style={{ ...S.btnSm, background: "#e8590c" }}
                          onClick={() =>
                            acceptMutation.mutate({ id: neg.id, ack: true })
                          }
                          disabled={acceptMutation.isPending}
                        >
                          I have checked this — create the PO anyway
                        </button>
                      </div>
                    </div>
                  )}
                  {neg.status === "active" && (
                    <button
                      style={S.btnGhost}
                      onClick={() => cancelMutation.mutate(neg.id)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    style={S.btnGhost}
                    onClick={() =>
                      setExpanded(expanded === neg.id ? null : neg.id)
                    }
                  >
                    {expanded === neg.id ? "Hide" : "Transcript"}
                  </button>
                </div>
              </div>
              {expanded === neg.id && (
                <div style={{ marginTop: 10 }}>
                  {neg.transcript.length === 0 ? (
                    <p style={{ ...S.muted, fontSize: 13 }}>
                      No transcript yet — the agent has not made its opening
                      offer.
                    </p>
                  ) : (
                    neg.transcript.map((turn, i) => (
                      <div
                        key={i}
                        style={{
                          marginTop: 6,
                          padding: "8px 12px",
                          background:
                            turn.role === "agent" ? "#e8eaf6" : "#f3e5f5",
                          borderRadius: 6,
                          borderLeft: `3px solid ${turn.role === "agent" ? "#3f51b5" : "#9c27b0"}`,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 12,
                            color: "#555",
                          }}
                        >
                          Round {turn.round} — {turn.role.toUpperCase()}
                          {turn.proposed_price !== null &&
                            ` · Proposed: $${turn.proposed_price?.toFixed(2)}`}
                        </div>
                        <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                          {turn.content}
                        </p>
                      </div>
                    ))
                  )}
                  {neg.status === "active" && (
                    <SimulateSupplierResponsePanel
                      key={neg.transcript.length}
                      neg={neg}
                      onResponded={() => void refetch()}
                    />
                  )}
                </div>
              )}
              {po && (
                <div style={{ marginTop: 8 }}>
                  <POStatusBanner
                    poNumber={po.po_number}
                    poStatus={po.po_status}
                    needsManager={po.po_needs_manager_approval}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
